import React, { useContext, useMemo, useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useFloating, offset, flip, shift, autoUpdate } from "@floating-ui/react";
import RemembranceForm from "../../components/form";
import {PendingItems} from "../../components/queue/pending";
import FinishedItem from "../../components/queue/finished";
import ProcessingItem from "../../components/queue/processing";
import { Input } from "antd";
import Loader from "../../components/loader";
import { APIProviderContext } from "../../components/api";
import { useSearchParams } from "react-router-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import classNames from "classnames";

const MAX_NUMBER_PER_CHARACTER = 102;
const CHARACTER_MAP = [
  "A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z",
  "A1","B1","C1","D1","E1","F1","G1","H1","I1","J1","K1","L1","M1","N1","O1","P1","Q1","R1","S1","T1","U1","V1","W1","X1","Y1","Z1",
];

function FormPage() {

  const [searchParams, setSearchParams] = useSearchParams();

  const [coordinate, setCoordinate] = useState(searchParams.get("fill") || "");

  const [viewData, setViewData] = useState(null);

  const transformWrapperRef = useRef(null);

  const { apiAction } = useContext(APIProviderContext);
  const [imageVersion, setImageVersion] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [animateModal, setAnimateModal] = useState(false);
  const containerRef = useRef(null);
  const mosaicRef = useRef(null);
  const [hoveredCoordinate, setHoveredCoordinate] = useState("");
  const [initialScale, setInitialScale] = useState(1);
  const [showPinchHint, setShowPinchHint] = useState(false);
  const { refs, floatingStyles } = useFloating({
    placement: "right",
    middleware: [offset(10), flip({ fallbackAxisSideDirection: "start" }), shift({ padding: 8 })],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
  });

  const introContainerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
  };

  const introItemVariants = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  useEffect(() => {
    if (showModal) {
      const id = setTimeout(() => setAnimateModal(true), 10);
      return () => clearTimeout(id);
    } else {
      setAnimateModal(false);
    }
  }, [showModal]);

  // Dynamically compute initial scale so a 1200px-wide image fits container
  useEffect(() => {
    const BASE_WIDTH = 1200; // defined image width
    function computeScale() {
      const containerWidth = containerRef.current?.clientWidth || 0;
      console.log("containerWidth", containerWidth);
      if (!containerWidth) {
        setInitialScale(1);
        return;
      }
      const scale = Math.min(1, Math.max(containerWidth / BASE_WIDTH, 0.1));
      setInitialScale(scale);
    }
    computeScale();
    const ro = new ResizeObserver(() => computeScale());
    if (containerRef.current) {
      ro.observe(containerRef.current);
    }
    return () => {
      try { ro.disconnect(); } catch (_) {}
    };
  }, [viewData]);

  // Show pinch hint for 2 seconds after the mosaic image becomes visible
  useEffect(() => {
    if (viewData?.photoUrl800) {
      setShowPinchHint(true);
      const id = setTimeout(() => setShowPinchHint(false), 3000);
      return () => clearTimeout(id);
    }
  }, [viewData?.photoUrl800]);

  function parseCoordinateToRowCol(input){
    if(!input){
      return null;
    }
    const trimmed = input.trim().toUpperCase();
    const match = trimmed.match(/^(\d{1,3})([A-Z](?:1)?)$/);
    if(!match){
      return null;
    }
    const numberPart = parseInt(match[1], 10);
    const characterPart = match[2];

    const rowIndex = CHARACTER_MAP.findIndex(c => c === characterPart);
    if(rowIndex === -1){
      return null;
    }
    const colIndex = Math.min(Math.max(numberPart, 1), 102) - 1; // 0-based
    return { rowIndex, colIndex };
  }

  const dotStyle = useMemo(() => {
    const rc = parseCoordinateToRowCol(coordinate);
    if(!rc){
      return { display: "none" };
    }
    const totalRows = 40;
    const totalCols = MAX_NUMBER_PER_CHARACTER;
    const topPct = ((rc.rowIndex + 0.5) / totalRows) * 100;
    const leftPct = ((rc.colIndex + 0.5) / totalCols) * 100;
    return {
      position: "absolute",
      bottom: `calc(${topPct}% - 8px)`,
      left: `calc(${leftPct}% - 8px)`,
      transform: "translate(-50%, -50%)",
      width: 16,
      height: 16,
      borderRadius: 9999,
      boxShadow: "0 0 0 4px rgb(15 89 133 / 72%)",
      background: "#0D72FF",
      animation: "blink 1s infinite",
      cursor: "pointer",
    };
  }, [coordinate]);

  // Floating UI-based label is anchored to the dot (reference element)
  console.log("initialScale", transformWrapperRef?.current?.instance?.transformState?.positionX);
  async function handleView(){
    const input = (coordinate || "").trim();
    if(!input){
      return;
    }
    setViewData({ loading: true });
    try {
      const data = await apiAction("GET", `task/lookup/coordinate/${encodeURIComponent(input)}`, {}, "json");
      setViewData({ loading: false, task: data?.task, photoUrl: data?.photoUrl, photoUrl800: data?.photoUrl800 });
      setImageVersion(Math.floor(Math.random() * 1000000000));
    } catch (err) {
      setViewData({ loading: false, error: err?.response?.data?.response || "Not found" });
    }
  }

  var relativeScale = ( transformWrapperRef?.current?.instance?.transformState?.scale ?? 1 )/ initialScale;

  // Once every x scale item
  var onceEvery, fontSize;
  
  if(relativeScale > 2){
    onceEvery = 1;
    fontSize = 10 / relativeScale;
  } else if(relativeScale > 1.5){
    onceEvery = 3;
    fontSize = 8 / relativeScale;
  } else if(relativeScale > 1){
    onceEvery = 5;
    fontSize = 8 / relativeScale;
  } else {
    onceEvery = 8;
    fontSize = 8 / relativeScale;
  }


  return (
    <div className={classNames({
      "flex flex-col min-h-screen w-full items-center p-4 py-10": true,
      "!w-[1200px]": viewData?.photoUrl800 || viewData?.photoUrl,
    })}>
      <motion.div
        className="w-full flex flex-col items-center"
        variants={introContainerVariants}
        initial="hidden"
        animate="show"
      >
        <motion.img
          variants={introItemVariants}
          src="/images/mosaic-image2.avif"
          className="w-[180px] h-auto mb-10"
        />
        <motion.h1 variants={introItemVariants} className="text-3xl font-bold mb-4 px-4 text-center ">
          View your spot in the mosaic
        </motion.h1>

        <div className="w-full p-[20px] mx-auto max-w-[1000px]">
      {
        viewData?.photoUrl800 && (
          <div className="relative w-full " ref={containerRef}>
            {showPinchHint && (
              <div 
              onMouseDown={()=>{
                setShowPinchHint(false);
              }}
              className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center bg-black/70">
                <DotLottieReact
                  src="/pin2.lottie"
                  autoplay
                  loop
                  style={{ width: 140, height: 140 }}
                />
              </div>
            )}
            <TransformWrapper
              key={initialScale}
              minScale={initialScale}
              initialScale={initialScale}
              ref={transformWrapperRef}
              centerOnInit
              wheel={{ step: 1 }}
              doubleClick={{  }}
              pinch={{ step: 5 }}
              panning={{step: 10}}
              limitToBounds={true}
              disablePadding={true}
              className="w-full relative aspect-[102/40]"
              style={{ width: "100%" }}
            >
              <div 
              
              className="absolute top-full left-0 w-full h-[20px] bg-[#0D72FF] flex items-center overflow-hidden">
                      <motion.div style={{
                        transformOrigin: "left center",
                        x: 1 * (transformWrapperRef?.current?.instance?.transformState?.positionX ?? 0),
                scale:relativeScale ,
              }} className="absolute top-0 left-0 w-full h-full flex items-center">
                      {Array.from({ length: MAX_NUMBER_PER_CHARACTER }).map((_, i) => (
                        <motion.div 
                        style={{
                          // Hide every 80% to 0% of index characters as scale goes from initialscale to >3
                          // So it looks like it skips some characters
                          opacity: i % onceEvery !== 0 ? 0 : 1,
                          fontSize: fontSize,
                        }}
                        key={`x-${i}`} 
                        className="flex items-center flex-1 justify-center h-[10px] text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tl-xl last:rounded-tr-xl relative">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            {i + 1}
                          </div>
                        </motion.div>
                      ))}
                      </motion.div>
                    </div>
                    <div 
                      className="absolute bottom-full left-0 w-full h-[20px] bg-[#0D72FF] flex items-center overflow-hidden">
                      <motion.div 
                        style={{
                          transformOrigin: "left center",
                          x: 1 * (transformWrapperRef?.current?.instance?.transformState?.positionX ?? 0),
                          scale: relativeScale,
                        }} 
                        className="absolute top-0 left-0 w-full h-full flex items-center">
                        {Array.from({ length: MAX_NUMBER_PER_CHARACTER }).map((_, i) => (
                          <motion.div 
                            style={{
                              opacity: i % onceEvery !== 0 ? 0 : 1,
                              fontSize: fontSize,
                            }}
                            key={`xt-${i}`} 
                            className="flex items-center flex-1 justify-center h-[10px] text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tl-xl last:rounded-tr-xl relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                              {i + 1}
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                    <div className="absolute right-full top-0 w-[20px] h-full bg-[#0D72FF] flex items-center flex-col-reverse overflow-hidden">
                      <motion.div
                        style={{
                          transformOrigin: "center top",
                          y: 1 * (transformWrapperRef?.current?.instance?.transformState?.positionY ?? 0),
                          scale: relativeScale,
                        }}
                        className="absolute top-0 left-0 w-full h-full flex flex-col-reverse items-center">
                        {Array.from({ length: 40 }).map((_, i) => (
                          <motion.div
                            style={{
                              opacity: i % onceEvery !== 0 ? 0 : 1,
                              fontSize: fontSize,
                            }}
                            key={`yl-${i}`}
                            className="flex items-center justify-center flex-1 w-full text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tl-xl last:rounded-bl-xl relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                              {CHARACTER_MAP[i]}
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                    <div className="absolute left-full top-0 w-[20px] h-full bg-[#0D72FF] flex items-center flex-col-reverse overflow-hidden">
                      <motion.div
                        style={{
                          transformOrigin: "center top",
                          y: 1 * (transformWrapperRef?.current?.instance?.transformState?.positionY ?? 0),
                          scale: relativeScale,
                        }}
                        className="absolute top-0 left-0 w-full h-full flex flex-col-reverse items-center">
                        {Array.from({ length: 40 }).map((_, i) => (
                          <motion.div
                            style={{
                              opacity: i % onceEvery !== 0 ? 0 : 1,
                              fontSize: fontSize,
                            }}
                            key={`yr-${i}`}
                            className="flex items-center justify-center flex-1 w-full text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tr-xl last:rounded-br-xl relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                              {CHARACTER_MAP[i]}
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                    {/* Corners that follow pan/zoom */}
                    <div className="absolute inset-0 pointer-events-none overflow-visible">
                      <motion.div
                        style={{
                          // transformOrigin: "left top",
                          // x: 1 * (transformWrapperRef?.current?.instance?.transformState?.positionX ?? 0),
                          // y: 1 * (transformWrapperRef?.current?.instance?.transformState?.positionY ?? 0),
                          // scale: relativeScale,
                        }}
                        className="absolute top-0 left-0 w-full h-full">
                        <div className="absolute -top-[20px] -left-[20px] w-[20px] h-[20px] bg-[#0D72FF] rounded-tl-xl" />
                        <div className="absolute -top-[20px] left-full w-[20px] h-[20px] bg-[#0D72FF] rounded-tr-xl" />
                        <div className="absolute top-full -left-[20px] w-[20px] h-[20px] bg-[#0D72FF] rounded-bl-xl" />
                        <div className="absolute top-full left-full w-[20px] h-[20px] bg-[#0D72FF] rounded-br-xl" />
                      </motion.div>
                    </div>
              <TransformComponent wrapperClass="w-full relative aspect-[102/40]">
                <div className="">
                  <div className="relative inline-block select-none">
                    <img
                      ref={mosaicRef}
                      src={"https://d3ukx5r8cxyox7.cloudfront.net/live-mosaic/mosaic.png?v=" + imageVersion}
                      alt="Mosaic"
                      className="w-[1200px] aspect-[102/40] max-w-[unset] block"
                      onMouseMove={(e) => {
                        const rect = mosaicRef.current?.getBoundingClientRect?.();
                        if(!rect || !rect.width || !rect.height){
                          setHoveredCoordinate("");
                          return;
                        }
                        const x = (e.clientX - rect.left) / rect.width; // 0..1 from left
                        const y = (e.clientY - rect.top) / rect.height; // 0..1 from top
                        const clampedX = Math.min(Math.max(x, 0), 1);
                        const clampedY = Math.min(Math.max(y, 0), 1);
                        const totalRows = 40;
                        const totalCols = MAX_NUMBER_PER_CHARACTER;
                        const colIndex = Math.min(totalCols - 1, Math.max(0, Math.floor(clampedX * totalCols)));
                        const rowIndexFromBottom = Math.min(totalRows - 1, Math.max(0, Math.floor((1 - clampedY) * totalRows)));
                        const rowLabel = CHARACTER_MAP[rowIndexFromBottom] || "";
                        const displayCol = colIndex + 1;
                        setHoveredCoordinate(`${displayCol}${rowLabel}`);
                      }}
                      onMouseLeave={() => setHoveredCoordinate("")}
                    />
                    {/* <div className="absolute top-full left-0 w-full h-[10px] bg-[#0D72FF] flex items-center">
                      {Array.from({ length: MAX_NUMBER_PER_CHARACTER }).map((_, i) => (
                        <div key={`x-${i}`} className="flex items-center flex-1 justify-center h-[10px] text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tl-xl last:rounded-tr-xl text-[3px] md:text-[5px]">
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    <div className="absolute bottom-full left-0 w-full h-[10px] bg-[#0D72FF] flex items-center">
                      {Array.from({ length: MAX_NUMBER_PER_CHARACTER }).map((_, i) => (
                        <div key={`x-${i}`} className="flex items-center flex-1 justify-center h-[10px] text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tl-xl last:rounded-tr-xl text-[3px] md:text-[5px]">
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    <div className="absolute right-full top-0 w-[10px] h-full bg-[#0D72FF] flex items-center flex-col-reverse">
                      {Array.from({ length: 42 }).map((_, i) => (
                        <div key={`x-${i}`} className="flex items-center flex-1 justify-center text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tl-xl last:rounded-tr-xl text-[3px] md:text-[5px]">
                          {CHARACTER_MAP[i]}
                        </div>
                      ))}
                    </div>
                    <div className="absolute left-full top-0 w-[10px] h-full bg-[#0D72FF] flex items-center flex-col-reverse">
                      {Array.from({ length: 42 }).map((_, i) => (
                        <div key={`x-${i}`} className="flex items-center flex-1 justify-center text-white bg-[#0D72FF] border-blue-700/60 first:rounded-tl-xl last:rounded-tr-xl text-[3px] md:text-[5px]">
                          {CHARACTER_MAP[i]}
                        </div>
                      ))}
                    </div> */}
                    {/* Corners */}
                    {/* <div className="absolute top-full left-full w-[10px] h-[10px] bg-[#0D72FF] rounded-br-xl"></div> */}
                    {/* <div className="absolute top-full right-full w-[10px] h-[10px] bg-[#0D72FF] rounded-bl-xl"></div> */}
                    {/* <div className="absolute bottom-full left-full w-[10px] h-[10px] bg-[#0D72FF] rounded-tr-xl"></div> */}
                    {/* <div className="absolute bottom-full right-full w-[10px] h-[10px] bg-[#0D72FF] rounded-tl-xl"></div> */}
                    <div
                      style={dotStyle}
                      ref={refs.setReference}
                      onClick={() => {
                        if(viewData?.photoUrl || viewData?.photoUrl800){
                          setShowModal(true);
                        }
                      }}
                      title={coordinate || ""}
                    />
                    {coordinate && (
                      <div ref={refs.setFloating} style={floatingStyles} className="z-10">
                        <div className="flex items-center gap-2">
                          <div className="bg-[#0D72FF] text-white text-[8px] md:text-[10px] px-3 py-[2px] rounded-full shadow-lg flex items-center gap-1">
                            <span className="opacity-90">YOU ARE HERE</span>
                            <span className="opacity-70">|</span>
                            <span className="font-semibold">{coordinate.toUpperCase()}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TransformComponent>
            </TransformWrapper>
            {hoveredCoordinate && (
              <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {hoveredCoordinate}
              </div>
            )}
          </div>
        )
      }
      </div>

        <motion.div variants={introItemVariants} className="w-full max-w-[1200px] p-4 flex flex-col max-w-[400px] mx-auto gap-4">
        <div className="mb-4">
            <label className="block text-[16px] mb-2">Coordinate</label>
            <Input
              placeholder="Eg. 12A"
              value={coordinate}
              maxLength={6}
              onChange={(e) => setCoordinate(e.target.value)}
              className="bg-blue-700 border-blue-600 text-white placeholder-blue-300"
            />
          </div>
          <button 
              onClick={() => handleView()} 
              className="w-full bg-[#0D72FF] text-white py-2 flex items-center justify-center px-6 h-12 font-medium hover:bg-[#0d5ecf] transition-colors mt-4  mb-6 rounded-lg"
            >
              {viewData?.loading ? <Loader /> : "View your spot"}
            </button>
            {/* Mosaic and dot overlay */}
            {viewData && !viewData?.loading && (
              <div className="w-full flex flex-col items-center gap-4">
                {viewData?.error && (
                  <div className="text-red-300 text-[16px]">{viewData.error}</div>
                )}
              </div>
            )}
            {showModal && (
              <div
                className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
                onClick={() => {
                  setAnimateModal(false);
                  setTimeout(() => setShowModal(false), 300);
                }}
              >
                <div className="bg-blue-900 p-4 rounded-lg relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    aria-label="Close"
                    onClick={() => {
                      setAnimateModal(false);
                      setTimeout(() => setShowModal(false), 300);
                    }}
                    className="absolute -top-5 -right-5 w-8 h-8 rounded-full bg-white text-blue flex text-[20px] items-center justify-center shadow-lg"
                  >
                    ×
                  </button>
                  {(viewData?.photoUrl800 || viewData?.photoUrl) ? (
                    <img
                      src={viewData?.photoUrl800 || viewData?.photoUrl}
                      alt="Your photo"
                      width={800}
                      height={800}
                      className={`max-w-[80vw] max-h-[80vh] object-contain rounded transition-opacity transition-transform duration-300 ease-in-out ${animateModal ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
                    />
                  ) : (
                    <div className="text-white">Photo not available</div>
                  )}
                </div>
              </div>
            )}
        </motion.div>
      </motion.div>
      <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </div>
  );
}

export default FormPage;